import { useEffect, useState } from "react";
import { fmtDate, fmtDateTime } from "@/lib/date";
import api from "@/lib/api";
import { Shell, Stat } from "@/components/Shell";
import { useAuth } from "@/context/AuthContext";
import { DayBoard } from "@/components/dashboard/DayBoard";
import { RolloverReminder } from "@/components/booklists/RolloverReminder";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { MiniList, NotTracked, Panel } from "@/components/dashboard/Panels";
import { SimpleBar } from "@/components/dashboard/Charts";

const OpsRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between border-b border-border pb-2">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="mono font-bold tabular-nums">{value ?? 0}</span>
  </div>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [day, setDay] = useState({ buckets: [] });

  useEffect(() => {
    api.get("/dashboard/admin").then((r) => setD(r.data)).catch(() => {});
    api.get("/dashboard/dayboard").then((r) => setDay(r.data)).catch(() => {});
  }, []);

  const c = d?.cards || {};
  const ops = d?.operations || {};
  const charts = d?.charts || {};
  const alerts = d?.alerts || {};

  return (
    <Shell title={`Good day, ${user?.name?.split(" ")[0] || ""}`}
      subtitle="Admin view — the full analytical picture across purchase, warehouse, dispatch and billing.">
      <RolloverReminder onRollover={() => nav("/booklists")} />
      <DayBoard buckets={day.buckets} />

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="admin-summary-cards">
        <Stat testid="admin-so-pending" label="SO Pending Qty" value={c.so_pending_qty} tone="warn" />
        <Stat testid="admin-ready-bundle" label="Schools Ready To Bundle" value={c.schools_ready_to_bundle} tone="good" />
        <Stat testid="admin-box-ids-pending" label="Box IDs Pending Dispatch" value={c.box_ids_pending_dispatch} tone="primary" />
        <Stat testid="admin-pod-pending" label="POD Pending" value={c.pod_pending} tone="bad" />
        <Stat testid="admin-overdue-pos" label="Overdue POs" value={c.overdue_pos} tone="bad" />
        <Stat testid="admin-open-followups" label="Open Follow-ups" value={c.open_followups} />
      </div>

      <h2 className="text-base md:text-lg font-bold mt-8 mb-3">Operations this month</h2>
      <div className="grid md:grid-cols-3 gap-4" data-testid="admin-operations">
        <div className="panel p-5" data-testid="ops-sales-orders">
          <div className="label-xs">Sales orders</div>
          <div className="mt-3 space-y-3">
            <OpsRow label="Raised" value={ops.sales_orders?.raised} />
            <OpsRow label="Billed" value={ops.sales_orders?.billed} />
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">Collected</span>
              <span className="text-xs font-bold text-muted-foreground">Not tracked yet</span>
            </div>
          </div>
        </div>
        <div className="panel p-5" data-testid="ops-purchase">
          <div className="label-xs">Purchase</div>
          <div className="mt-3 space-y-3">
            <OpsRow label="POs raised" value={ops.purchase?.pos_raised} />
            <OpsRow label="GRNs received" value={ops.purchase?.grns_received} />
          </div>
        </div>
        <div className="panel p-5" data-testid="ops-dispatch">
          <div className="label-xs">Dispatch</div>
          <div className="mt-3 space-y-3">
            <OpsRow label="Batches sent" value={ops.dispatch?.batches_sent} />
            <OpsRow label="POD confirmed" value={ops.dispatch?.pod_confirmed} />
          </div>
        </div>
      </div>

      <h2 className="text-base md:text-lg font-bold mt-8 mb-3">Trends</h2>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="panel p-5 min-w-0">
          <div className="label-xs mb-2">Monthly dispatch trend · boxes</div>
          <SimpleBar data={charts.dispatch_trend} xKey="label" barKey="boxes" testid="chart-dispatch-trend" />
        </div>
        <div className="panel p-5 min-w-0">
          <div className="label-xs mb-2">Publisher-wise pending stock · qty on order</div>
          <div className="text-sm font-bold mb-2" data-testid="chart-publisher-total">
            {charts.publisher_pending_total || 0} publisher(s) with stock on order ·
            showing the top {(charts.publisher_pending_stock || []).length}
          </div>
          <SimpleBar data={charts.publisher_pending_stock} xKey="publisher" barKey="pending_qty"
            color="#B4530A" testid="chart-publisher-pending" />
          <Link to="/purchase-orders" data-testid="chart-publisher-link"
            className="inline-block mt-3 text-xs font-semibold text-[#0055FF] hover:underline">
            View all →
          </Link>
        </div>
      </div>

      <div className="panel mt-4 overflow-x-auto" data-testid="school-pending-demand">
        <div className="px-5 py-4 border-b border-border flex flex-wrap items-baseline justify-between gap-2">
          <span className="label-xs">School-wise pending demand</span>
          <span className="text-sm font-bold" data-testid="spd-total">
            {charts.school_pending_total || 0} school(s) pending ·
            showing the top {(charts.school_pending_demand || []).length}
            <Link to="/pending-report" data-testid="spd-link"
              className="ml-3 text-xs font-semibold text-[#0055FF] hover:underline">View all →</Link>
          </span>
        </div>
        <table className="dtable">
          <thead><tr><th>School</th><th className="text-right">Classes</th>
            <th className="text-right">Demand</th><th className="text-right">Pending</th></tr></thead>
          <tbody>
            {(charts.school_pending_demand || []).map((r) => (
              <tr key={r.school_name} data-testid={`spd-${r.school_name}`}>
                <td className="font-medium">{r.school_name}</td>
                <td className="num">{r.classes}</td>
                <td className="num">{r.demand_qty}</td>
                <td className="num text-[#B4530A] font-bold">{r.pending_qty}</td>
              </tr>
            ))}
            {!(charts.school_pending_demand || []).length && (
              <tr><td colSpan={4} className="empty-state">No pending school demand.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <h2 className="text-base md:text-lg font-bold mt-8 mb-3">Alerts</h2>
      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="Low stock titles" count={alerts.low_stock_total} to="/stock"
          testid="alert-low-stock" footer="View all"
          headline={`${alerts.low_stock_total || 0} title(s) below their pending demand`}>
          <MiniList rows={alerts.low_stock} empty="Every title covers its demand."
            left={(r) => <><span className="font-medium">{r.title}</span>
              <span className="text-muted-foreground text-xs"> · free {r.free}</span></>}
            right={(r) => `short ${r.short_by}`} />
        </Panel>
        <Panel title="Overdue POs by supplier" count={alerts.overdue_by_supplier_total}
          to="/purchase-orders" testid="alert-overdue-pos" footer="View all"
          headline={`${alerts.overdue_by_supplier_total || 0} supplier(s) past the expected date`}>
          <MiniList rows={alerts.overdue_by_supplier} empty="No PO is past its delivery date."
            left={(r) => <><span className="font-medium">{r.publisher}</span>
              <span className="text-muted-foreground text-xs"> · {r.pos} PO(s)</span></>}
            right={(r) => r.pending_qty} />
        </Panel>
        <Panel title="Schools with unbilled orders" count={alerts.schools_unbilled_total}
          to="/pending-billing" testid="alert-unbilled" footer="View all"
          headline={`${alerts.schools_unbilled_total || 0} order(s) still to bill`}>
          <MiniList rows={alerts.schools_unbilled} empty="Every order is billed."
            left={(r) => <><span className="font-medium">{r.party_name}</span>
              <span className="mono text-muted-foreground text-xs"> · {r.so_no}</span></>}
            right={(r) => r.balance_qty} />
        </Panel>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 panel min-w-0" data-testid="activity-feed">
          <div className="px-5 py-4 border-b border-border label-xs">Recent activity · last 10 actions</div>
          <div className="divide-y divide-border">
            {(d?.activity || []).map((a, i) => (
              <div key={`${a.at}-${a.action}-${a.entity}-${i}`} className="px-5 py-2.5 flex items-baseline justify-between gap-3 text-sm min-w-0"
                data-testid={`activity-${i}`}>
                <span className="truncate min-w-0">
                  <span className="font-medium">{a.action}</span>
                  <span className="text-muted-foreground"> · {a.entity} · {a.user_name}</span>
                </span>
                <span className="mono text-xs text-muted-foreground shrink-0">{fmtDateTime(a.at)}</span>
              </div>
            ))}
            {!(d?.activity || []).length && <div className="empty-state">No activity recorded yet.</div>}
          </div>
        </div>
        <NotTracked testid="admin-collections-note" label="Collections"
          hint="Payments, receipts and COD are not recorded in the app yet — these need a payments module." />
      </div>

      <div className="text-xs text-muted-foreground mt-4">
        Figures as of {fmtDate(new Date().toISOString())}.
      </div>
    </Shell>
  );
}
